import { createSelector } from 'reselect';

import {
    selectCurrency,
    selectLocale,
    i18nMoney,
} from 'wanderu-i18n';

import { selectAppStateSlice } from '../selectors';
import NAMESPACE from './types';

const INTERLINK_MAP = {
    c2c_depart: 'depart',
    c2c_arrive: 'arrive',
    c2c_popular: 'popular',
};

function getData(appState) {
    return selectAppStateSlice(appState)[NAMESPACE];
}

function getInterlinkData(appState, id) {
    return getData(appState)[id];
}

function mapC2CData(interlinkData, _locale, _currency) {
    if (!interlinkData) {
        return undefined;
    }

    return interlinkData.reduce((data, c2cLink) => {
        /* decide which tab should own this "list" of links */
        const label = INTERLINK_MAP[c2cLink.recommended_basis];

        /* find the lowest avg price across all <mode> carriers on this route */
        const minPrice = Object.values(c2cLink.carriers_stat)
            .reduce((minPrice, carrier) => {
                return carrier.avg_price_usd < minPrice
                    ? carrier.avg_price_usd
                    : minPrice;
            }, Infinity);

        const price = i18nMoney(minPrice)
            .format({
                maximumFractionDigits: 0,
                minimumFractionDigits: 0,
            });
        const adornment = `${price}+`;

        return {
            ...data,
            [label]: [
                ...data[label],
                {
                    linkAnchorText: c2cLink.anchor_text,
                    linkAdornment: adornment,
                    linkUrl: c2cLink.target_url_slug,
                },
            ],
        };
    }, { arrive: [], depart: [], popular: [] });
}

function _mapInterlinksToArriveCity(interlinkData) {
    return interlinkData.reduce((_tabData, link) => {
        const {
            anchor_text,
            arrive_city,
            min_avg_price_usd,
            target_url_slug,
            trans_mode: transMode,
        } = link;

        const splitUrl = target_url_slug.split('/');
        const arriveRegion = splitUrl[4];
        const arriveCity = splitUrl[5];
        const placeGuideUrl = `/${transMode}-tickets/${arriveRegion}/${arriveCity}/`;

        const cityData = {
            placeGuideUrl,
            url: target_url_slug,
            title: anchor_text,
            price: min_avg_price_usd,
        };

        if (!_tabData[arrive_city]) {
            return {
                ..._tabData,
                [arrive_city]: { [transMode]: [cityData] },
            };
        }

        if (!_tabData[arrive_city][transMode]) {
            return {
                ..._tabData,
                [arrive_city]: {
                    ..._tabData[arrive_city],
                    [transMode]: [cityData],
                },
            };
        }

        _tabData[arrive_city][transMode].push(cityData);
        return _tabData;
    }, {});
}

function mapHomepageData(data, locale) {
    const interlinkData = data[`homepage-${locale}`];
    if (!interlinkData) {
        return undefined;
    }

    const cityData = _mapInterlinksToArriveCity(interlinkData);
    return Object.keys(cityData).map((tab) => ({ header: tab, data: cityData[tab] }));
}

const selectC2CData = createSelector(
    getInterlinkData,
    selectLocale,
    selectCurrency,
    mapC2CData,
);

const selectHomepageData = createSelector(
    getData,
    selectLocale,
    mapHomepageData,
);

export {
    selectC2CData,
    selectHomepageData,
};
